import { db } from "../firebaseFunctions/config";
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  increment,
  arrayUnion
} from "firebase/firestore";

const REFERRAL_DISCOUNT_PERCENT = 5;
const REFERRER_REWARD_PERCENT = 5;

export const referralService = {
  async checkReferralDiscount(userId) {
    try {
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return { hasDiscount: false, error: "User not found" };
      }

      const userData = userDoc.data();
      
      if (!userData.referrerId) {
        return { hasDiscount: false, reason: "No referral code used during signup" };
      }

      if (userData.referralDiscountUsed) {
        return { hasDiscount: false, reason: "Referral discount already used" };
      }

      const referrerRef = doc(db, "users", userData.referrerId);
      const referrerDoc = await getDoc(referrerRef);

      return { 
        hasDiscount: true, 
        discountPercent: REFERRAL_DISCOUNT_PERCENT,
        referrerId: userData.referrerId,
        referrerName: referrerDoc.exists() ? (referrerDoc.data().businessName || referrerDoc.data().email) : "Unknown"
      };
    } catch (error) {
      console.error("Error checking referral discount:", error);
      return { hasDiscount: false, error: error.message };
    }
  },

  calculateDiscountedFee(originalFee, discountPercent = REFERRAL_DISCOUNT_PERCENT) {
    const discountAmount = originalFee * (discountPercent / 100);
    const discountedFee = originalFee - discountAmount;
    return {
      originalFee,
      discountPercent,
      discountAmount: Math.round(discountAmount * 100) / 100,
      discountedFee: Math.round(discountedFee * 100) / 100
    };
  },

  calculateReferrerReward(serviceFee, rewardPercent = REFERRER_REWARD_PERCENT) {
    const rewardAmount = serviceFee * (rewardPercent / 100);
    return Math.round(rewardAmount * 100) / 100;
  },

  async applyReferralDiscount(userId, eventId, originalServiceFee) {
    try {
      const discountCheck = await this.checkReferralDiscount(userId);
      
      if (!discountCheck.hasDiscount) {
        return { 
          applied: false, 
          reason: discountCheck.reason || discountCheck.error,
          finalFee: originalServiceFee
        };
      }

      const feeCalculation = this.calculateDiscountedFee(originalServiceFee);
      const referrerReward = this.calculateReferrerReward(originalServiceFee);

      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        referralDiscountUsed: true,
        referralDiscountUsedAt: serverTimestamp(),
        referralDiscountEventId: eventId,
        referralDiscountAmount: feeCalculation.discountAmount,
        updatedAt: serverTimestamp()
      });

      const referrerRef = doc(db, "users", discountCheck.referrerId);
      await updateDoc(referrerRef, {
        referralCount: increment(1),
        referralRewards: arrayUnion({
          referredUserId: userId,
          eventId: eventId,
          rewardAmount: referrerReward,
          originalServiceFee: originalServiceFee,
          createdAt: new Date().toISOString(),
          status: "pending"
        }),
        totalReferralEarnings: increment(referrerReward),
        updatedAt: serverTimestamp()
      });

      const referralRecordRef = doc(collection(db, "referralTransactions"));
      await setDoc(referralRecordRef, {
        referrerId: discountCheck.referrerId,
        referredUserId: userId,
        eventId: eventId,
        originalServiceFee: originalServiceFee,
        discountPercent: REFERRAL_DISCOUNT_PERCENT,
        discountAmount: feeCalculation.discountAmount,
        discountedFee: feeCalculation.discountedFee,
        referrerRewardPercent: REFERRER_REWARD_PERCENT,
        referrerRewardAmount: referrerReward,
        status: "completed",
        createdAt: serverTimestamp()
      });

      return {
        applied: true,
        originalFee: originalServiceFee,
        discountPercent: REFERRAL_DISCOUNT_PERCENT,
        discountAmount: feeCalculation.discountAmount,
        finalFee: feeCalculation.discountedFee,
        referrerReward: referrerReward
      };
    } catch (error) {
      console.error("Error applying referral discount:", error);
      return { 
        applied: false, 
        error: error.message,
        finalFee: originalServiceFee
      };
    }
  },

  async getReferralStats(userId) {
    try {
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return { success: false, error: "User not found" };
      }

      const userData = userDoc.data();
      
      return {
        success: true,
        referralCode: userData.referralCode,
        referralCount: userData.referralCount || 0,
        totalEarnings: userData.totalReferralEarnings || 0,
        pendingRewards: (userData.referralRewards || []).filter(r => r.status === "pending"),
        paidRewards: (userData.referralRewards || []).filter(r => r.status === "paid"),
        hasUsedReferralDiscount: userData.referralDiscountUsed || false,
        referralDiscountAmount: userData.referralDiscountAmount || 0
      };
    } catch (error) {
      console.error("Error getting referral stats:", error);
      return { success: false, error: error.message };
    }
  },

  async getReferralHistory(userId) {
    try {
      const transactionsRef = collection(db, "referralTransactions");
      
      const asReferrerQuery = query(transactionsRef, where("referrerId", "==", userId));
      const asReferredQuery = query(transactionsRef, where("referredUserId", "==", userId));
      
      const [referrerSnapshot, referredSnapshot] = await Promise.all([
        getDocs(asReferrerQuery),
        getDocs(asReferredQuery)
      ]);

      const asReferrer = referrerSnapshot.docs.map(doc => ({
        id: doc.id,
        type: "earned",
        ...doc.data()
      }));

      const asReferred = referredSnapshot.docs.map(doc => ({
        id: doc.id,
        type: "received",
        ...doc.data()
      }));

      return {
        success: true,
        referrals: asReferrer,
        discountsReceived: asReferred,
        totalEarned: asReferrer.reduce((sum, r) => sum + (r.referrerRewardAmount || 0), 0),
        totalSaved: asReferred.reduce((sum, r) => sum + (r.discountAmount || 0), 0)
      };
    } catch (error) {
      console.error("Error getting referral history:", error);
      return { success: false, error: error.message };
    }
  },

  async storeReferralAtSignup(userId, referralCode, referrerId) {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        usedReferralCode: referralCode,
        referrerId: referrerId,
        referralDiscountUsed: false,
        referralDiscountAvailable: true,
        referralSignupDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error("Error storing referral at signup:", error);
      return { success: false, error: error.message };
    }
  }
};
